import { constants, isFeatureEnabled, Profile } from '@trade-platform/ui-shared';

import { profileHasRoles } from '../routing/guards/profile-checklist.guard';
import { profileFeatures } from './constants';

/**
 * Who may open the order Admin tab, and what they see once they are in it (roadmap#333).
 *
 * Two audiences reach the same panel by two different flags:
 *
 *   orderAdmin role + orderAdmin feature        AIX operations, the full panel
 *   sysAdmin role + clientOrderAdmin feature    a client tenant, two actions
 *
 * These pairs are deliberately NARROWER than admin-api's gate
 * (services/admin-api/lib/orderAdminGate.js), and the direction is the point: an identity admitted
 * here clears that gate's role and feature checks too, never the reverse. That is what keeps the
 * panel from offering an action the caller has no standing to take.
 *
 * Necessary, not sufficient. The API applies checks this code cannot make — entity scoping among
 * them — so a 403 stays reachable for a caller these predicates admit, and a 403 is not a message
 * the caller reads and moves on from: UnauthorizedInterceptor ends the session on any of them,
 * before the action's own failure handler runs. So admission here is not a promise the call will
 * succeed, and anything this panel can already tell will be refused must not be called at all.
 *
 * Two identities that gate's role and feature checks would pass are left out on purpose: a sysAdmin
 * holding the orderAdmin feature, and the orderAdmin role holding only clientOrderAdmin. Its role
 * list is shared across four routes rather than drawn per audience, and neither identity is one
 * roadmap#333 set out to serve — so do not "fix" the asymmetry by widening these to match it.
 *
 * No clause-by-clause restatement of that gate here, deliberately: it lives in another repository
 * and ships on its own cadence, so a copy of its structure goes stale with nothing failing to say
 * so. What is safe to record is the direction above, which survives the gate being tightened.
 *
 * `orderAdmin` is BOTH a role name and a feature name; they come from different fields on the
 * profile and both halves are required. Holding the role with the feature off is the dark-launch
 * state, not an entitlement.
 *
 * These read the profile out of localStorage via isFeatureEnabled, so call them from a getter rather
 * than a field initializer — a field would freeze the answer at construction time.
 */
export const isAixOrderAdmin = (profile: Profile) =>
    profileHasRoles([{ name: constants.profileTypes.ORDER_ADMIN }], profile) &&
    isFeatureEnabled(profileFeatures.orderAdmin);

/**
 * A client tenant's own System Administrator, in an organization granted clientOrderAdmin.
 *
 * roadmap#333 scopes the delegation to "client system administrators", so `sysAdmin` alone — not the
 * ordinary firm `admin` role, which is held by most firm users with any administrative duty and
 * which admin-api's gate also refuses.
 */
export const isClientOrderAdmin = (profile: Profile) =>
    profileHasRoles([{ name: constants.profileTypes.SYSADMIN }], profile) &&
    isFeatureEnabled(profileFeatures.clientOrderAdmin);

/** May the Admin tab be shown at all. */
export const canAccessOrderAdmin = (profile: Profile) =>
    isAixOrderAdmin(profile) || isClientOrderAdmin(profile);

/**
 * True when the caller is admitted ONLY as a client order admin, which is what narrows the panel to
 * the two delegated actions. Phrased against isAixOrderAdmin rather than as "is a client" because a
 * user who holds both must keep the full panel — the API would let them through either way, so
 * hiding cards from them would be a UI-only restriction the backend does not share.
 */
export const isClientOrderAdminOnly = (profile: Profile) =>
    isClientOrderAdmin(profile) && !isAixOrderAdmin(profile);
